import React, { useState } from "react";
import { Button, Grid, Box, Typography, Stack } from "@mui/material";
import "./Landing.scss";
import { useTranslation } from "react-i18next";
import TopMenu from "../../../components/Menu/top-menu";
import userService from "../../../services/userService";
import Application from "../../../components/Application";
import jsUtils from "../../../utils/jsUtils";
import config from "../../../config";

const ICON_BASE_URL = config.ICON_BASE_URL;
const LMS_IVV_ID_PREFIX = "App-LMS IV/V";
const LMS_IVV_DEFAULT_ICON = "FE-V-icon.png";

export default function Landing({
  userInfo,
  onMenuItemClicked,
  showToastMessage,
}) {
  const { t } = useTranslation();
  const [coyoteCssClass, setCoyoteCssClass] = React.useState(false);
  const [modifiedUserInfo, setModifiedUserInfo] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const navigateToUserManagement = () => {
    const userManagementUrl = userService.getUserManagementRouteUrl(
      localStorage.getItem("sid"),
      localStorage.getItem("timeOut"),
      global?.federatedType || null,
    );
    window.location.assign(userManagementUrl);
  };

  const getName = () => {
    if (userInfo && (userInfo.firstName || userInfo.lastName)) {
      if (userInfo.firstName && userInfo.lastName) {
        return userInfo.firstName + " " + userInfo.lastName;
      } else {
        return userInfo.firstName || userInfo.lastName;
      }
    } else {
      return "";
    }
  };

  const navigateTo = (_url) => {
    let sid = localStorage.getItem("sid");
    const gmtOffset = jsUtils.getGMTOffset();
    if (sid && _url) {
      window.open(
        `${_url}?sid=${sid}&gmt_offset=${gmtOffset}`,
        "_blank",
        "noopener",
      );
    }
  };
  const navigateToNextGen = () => {
    let sid = localStorage.getItem("sid");
    window.location.assign(
      `${config.baseUrlMicrofrontend}/TimeEntry#sid=${sid}`,
    );
  };
  const navigateToNewOpenWindowLMS = (appId) => {
    loadHTMLFormatSAMLIDPLMS(appId);
  };
  const navigateToNewOpenWindowCAC = () => {
    loadHTMLFormatSAMLIDPCAC();
  };
  React.useEffect(() => {
    if (userInfo && userInfo.groupsNames) {
      let coyoteClassCac = userInfo.groupsNames.findIndex(
        (group) => group === "App-CAC",
      );
      let coyoteClassNxtGen = userInfo.groupsNames.findIndex(
        (group) => group === "App-NextGen",
      );
      let coyoteClassCaHNew = userInfo.groupsNames.findIndex(
        (group) => group === "App-CAH-New",
      );
      let coyoteClassCaHVMW = userInfo.groupsNames.findIndex(
        (group) => group === "App-CAH-Horizon",
      );

      if (
        coyoteClassCac > -1 ||
        coyoteClassNxtGen > -1 ||
        coyoteClassCaHNew > -1 ||
        coyoteClassCaHVMW > -1
      ) {
        setCoyoteCssClass("coyoteCssClassCover");
      } else {
        setCoyoteCssClass("landingPageCover");
      }
    }
  }, [userInfo]);

  const getLmsIvVLabelLine2 = (appId) => {
    const suffix = (appId || "")
      .replace(LMS_IVV_ID_PREFIX, "")
      .replace(/^[-/:_\s]+/, "")
      .trim();

    return suffix ? `IV/V ${suffix}` : "IV/V";
  };

  const getLmsIvVIconFileName = (appId) => {
    const suffix = (appId || "")
      .replace(LMS_IVV_ID_PREFIX, "")
      .replace(/^[-/:_\s]+/, "")
      .trim();

    if (!suffix) {
      return LMS_IVV_DEFAULT_ICON;
    }

    const normalizedSuffix = suffix
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    return normalizedSuffix
      ? `FE-V-icon-${normalizedSuffix}.png`
      : LMS_IVV_DEFAULT_ICON;
  };

  const moveLmsIvVAppsToFront = (groups) => {
    const exactLmsIvVGroups = groups.filter(
      (group) => group?.id === LMS_IVV_ID_PREFIX,
    );
    const lmsIvVVariantGroups = groups.filter(
      (group) =>
        group?.id?.startsWith(LMS_IVV_ID_PREFIX) &&
        group?.id !== LMS_IVV_ID_PREFIX,
    );
    const otherGroups = groups.filter(
      (group) => !group?.id?.startsWith(LMS_IVV_ID_PREFIX),
    );

    return [...lmsIvVVariantGroups, ...exactLmsIvVGroups, ...otherGroups];
  };

  const getApplicationDetails = (_applicationGroup) => {
    const appId = _applicationGroup?.id;
    if (appId?.startsWith(LMS_IVV_ID_PREFIX)) {
      const fallbackImgSrc = `${ICON_BASE_URL}${LMS_IVV_DEFAULT_ICON}`;
      return {
        imgSrc: `${ICON_BASE_URL}${getLmsIvVIconFileName(appId)}`,
        fallbackImgSrc,
        label: (
          <>
            Finance Enterprise
            <br />
            {getLmsIvVLabelLine2(appId)}
          </>
        ),
        url: _applicationGroup?.url,
      };
    }
    switch (appId) {
      case "App-NextGen":
        return {
          imgSrc: `${ICON_BASE_URL}CPMAttorneyWorkspace-icon.png`,
          label: t("NEXTGEN_ICON_LABEL"),
          subText: t("TIME_ENTRY_CLIENT_ENQUIRY"),
          url: _applicationGroup?.url,
        };
      case "App-CAC":
        return {
          imgSrc: `${ICON_BASE_URL}CoyoteAnalyticsExclusive-icon.png`,
          label: t("COYOTE_ANALYTICS"),
          subText: t("PRACTICE_MANAGEMENT_REPORTS"),
          url: _applicationGroup?.url,
        };
      case "App-LMSPlus":
        return {
          imgSrc: `${ICON_BASE_URL}FE-Plus-icon.png`,
          label: (
            <>
              {t("LMS_ACCOUNT")}
              <br />
              {_applicationGroup.label}
            </>
          ),
          url: _applicationGroup.url,
        };

      case "App-QuickSight":
        return {
          imgSrc: `${ICON_BASE_URL}mobile_icon_kpi_white_2.png`,
          label: t("VIEW_KPI"),
          url: _applicationGroup?.url || "https://quicksight.aws.amazon.com/",
        };
      case "App-CAH-New":
        return {
          imgSrc: `${ICON_BASE_URL}CoyoteAnalyticsExclusive-icon.png`,
          label: t("APPSTREAM_CAH_NEW"),
          subText: t("PRACTICE_MANAGEMENT_REPORTS"),
          url: _applicationGroup?.url,
        };
      case "App-CAH-Horizon":
        return {
          imgSrc: `${ICON_BASE_URL}Horizon.png`,
          label: t("COYOTE_ANALYTICS"),
          subText: t("PRACTICE_MANAGEMENT_REPORTS"),
          url: _applicationGroup?.url,
        };
      default:
        return { imgSrc: null, label: null, url: null };
    }
  };

  const navigateToOpenVMW = () => {
    let myWindow = window.open("", "_blank");
    let dom = myWindow?.document;
    const endpoint = `/sso/v1/saml?app=App-CAH-Horizon`;
    userService
      .getHtmlFormatSAMLIDP(endpoint)
      .then((response) => {
        if (
          response?.data &&
          !(response?.data instanceof Error) &&
          response?.data?.data
        ) {
          let htmlFormat = response?.data?.data ?? "";
          htmlFormat = htmlFormat
            .replace(/'/g, "'")
            .replace(/"/g, '"')
            .replace(/\r\t/g, "")
            .replace(/\r\n/g, "")
            .replace(/\n/g, "");
          // setHtmlFormatSAMLIDP(htmlFormat);
          // openWindow (htmlFormat);
          dom?.open();
          dom?.write(htmlFormat);
          dom?.close();
        } else if (
          response?.data &&
          Number(response?.data.status) !== 200 &&
          response.data.error.length > 0
        ) {
          const errs = jsUtils.parseError(response.data.error);
          myWindow?.close();
          showToastMessage(errs);
        } else {
          myWindow?.close();
          showToastMessage(t("AN_UNEXPECTED_ERROR_HAS_OCCURRED"));
        }
      })
      .catch(() => {
        myWindow?.close();
        showToastMessage(t("AN_UNEXPECTED_ERROR_HAS_OCCURRED"));
      });
  };
  const loadHTMLFormatSAMLIDPLMS = (appId) => {
    let myWindow = window.open("", "_blank");
    let dom = myWindow?.document;
    const subgroup = appId
      ?.replace("App-LMS IV/V", "")
      .replace(/^-/, "")
      .trim();
    const endpoint = subgroup ? `/sso/v1/saml?app=${appId}` : `/sso/v1/saml`;
    userService
      .getHtmlFormatSAMLIDP(endpoint)
      .then((response) => {
        if (
          response?.data &&
          !(response?.data instanceof Error) &&
          response?.data?.data
        ) {
          let htmlFormat = response?.data?.data ?? "";
          htmlFormat = htmlFormat
            .replace(/'/g, "'")
            .replace(/"/g, '"')
            .replace(/\r\t/g, "")
            .replace(/\r\n/g, "")
            .replace(/\n/g, "");
          dom?.open();
          dom?.write(htmlFormat);
          dom?.close();
        } else if (
          response?.data &&
          Number(response?.data.status) !== 200 &&
          response.data.error.length > 0
        ) {
          const errs = jsUtils.parseError(response.data.error);
          myWindow?.close();
          showToastMessage(errs);
        } else {
          myWindow?.close();
          showToastMessage(t("AN_UNEXPECTED_ERROR_HAS_OCCURRED"));
        }
      })
      .catch(() => {
        myWindow?.close();
        showToastMessage(t("AN_UNEXPECTED_ERROR_HAS_OCCURRED"));
      });
  };
  const loadHTMLFormatSAMLIDPCAC = () => {
    let myWindow = window.open("", "_blank");
    let dom = myWindow?.document;
    const endpoint = `/sso/v1/saml`;
    userService
      .getHtmlFormatSAMLIDP(endpoint)
      .then((response) => {
        if (
          response?.data &&
          !(response?.data instanceof Error) &&
          response?.data?.data
        ) {
          let htmlFormat = response?.data?.data ?? "";
          htmlFormat = htmlFormat
            .replace(/'/g, "'")
            .replace(/"/g, '"')
            .replace(/\r\t/g, "")
            .replace(/\r\n/g, "")
            .replace(/\n/g, "");
          dom?.open();
          dom?.write(htmlFormat);
          dom?.close();
        } else if (
          response?.data &&
          Number(response?.data.status) !== 200 &&
          response.data.error.length > 0
        ) {
          const errs = jsUtils.parseError(response.data.error);
          myWindow?.close();
          showToastMessage(errs);
        } else {
          myWindow?.close();
          showToastMessage(t("AN_UNEXPECTED_ERROR_HAS_OCCURRED"));
        }
      })
      .catch(() => {
        myWindow?.close();
        showToastMessage(t("AN_UNEXPECTED_ERROR_HAS_OCCURRED"));
      });
  };

  const navQuickSightEmbeddedUrl = () => {
    let sid = localStorage.getItem("sid");
    window.location.assign(
      `${config.baseUrlMicrofrontend}/Dashboards#sid=${sid}`,
    );
  };

  const loadHTMLFormatAppStream = () => {
    let myWindow = window.open("", "_blank");
    let dom = myWindow?.document;
    const endpoint = `/sso/v1/saml?app=App-CAH-New`;
    userService
      .getHtmlFormatSAMLIDP(endpoint)
      .then((response) => {
        if (
          response?.data &&
          !(response?.data instanceof Error) &&
          response?.data?.data
        ) {
          let htmlFormat = response?.data?.data ?? "";
          htmlFormat = htmlFormat
            .replace(/'/g, "'")
            .replace(/"/g, '"')
            .replace(/\r\t/g, "")
            .replace(/\r\n/g, "")
            .replace(/\n/g, "");
          // setHtmlFormatSAMLIDP(htmlFormat);
          // openWindow (htmlFormat);
          dom?.open();
          dom?.write(htmlFormat);
          dom?.close();
        } else if (
          response?.data &&
          Number(response?.data.status) !== 200 &&
          response.data.error.length > 0
        ) {
          const errs = jsUtils.parseError(response.data.error);
          myWindow?.close();
          showToastMessage(errs);
        } else {
          myWindow?.close();
          showToastMessage(t("AN_UNEXPECTED_ERROR_HAS_OCCURRED"));
        }
      })
      .catch(() => {
        myWindow?.close();
        showToastMessage(t("AN_UNEXPECTED_ERROR_HAS_OCCURRED"));
      });
  };
  const renderApplications = (_userInfo) => {
    let parentContainer = "";
    let applicationGroups = _userInfo?.apps || [];
    // eslint-disable-next-line no-unused-vars
    const isArrayOfStrings = (obj) =>
      Array.isArray(obj) && obj.every((item) => typeof item === "string");

    // Process User info to create individual LMS+ entries in apps.
    if (!modifiedUserInfo && !applicationGroups.some((obj) => "url" in obj)) {
      applicationGroups.map((appgroup) => {
        if (
          appgroup?.id === "App-LMSPlus" &&
          appgroup?.namedUrls &&
          appgroup.namedUrls.length > 0
        ) {
          // For LMSPlus, use namedUrls instead of urls
          appgroup.namedUrls.forEach((namedUrl) => {
            applicationGroups.push({
              id: appgroup.id,
              url: namedUrl.url,
              subText: namedUrl.url,
              label: namedUrl.name,
            });
          });
        } else if (appgroup?.urls && appgroup?.urls.length > 0) {
          appgroup?.urls.map((item, index) => {
            applicationGroups.push({
              id: appgroup.id,
              url: appgroup?.urls[index],
              subText: appgroup?.urls[index],
            });
          });
        } else {
          applicationGroups.push({ id: appgroup.id, url: "" });
        }
      });
      /// applicationGroups = applicationGroups.filter(item => item.url);
      setModifiedUserInfo(true);
    }
    applicationGroups = applicationGroups.filter(
      (item) => typeof item.url === "string",
    );
    applicationGroups = moveLmsIvVAppsToFront(applicationGroups);

    if (coyoteCssClass === "coyoteCssClassCover") {
      parentContainer = "coyoteContainer";
    }
    if (applicationGroups.length) {
      let lmsPlusIconCount = applicationGroups.filter(
        (x) => x.id === "App-LMSPlus",
      ).length;
      const hasCAC = userInfo.groupsNames.includes("App-CAC");
      const hasCAHNew = userInfo.groupsNames.includes("App-CAH-New");
      const hasVMWare = userInfo.groupsNames.includes("App-CAH-Horizon");

      return (
        <Box sx={{ flexGrow: 1 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="baseline"
            className={parentContainer}
          >
            {applicationGroups.map((group) => {
              let { imgSrc, fallbackImgSrc, label, url, subText } =
                getApplicationDetails(group);
              if (group.id === "App-LMSPlus" && lmsPlusIconCount === 1) {
                label = "Finance Enterprise +";
              }

              const isLMSIVV = group.id?.startsWith("App-LMS IV/V");
              const tooltipText = isLMSIVV
                ? "Using Finance Enterprise IV/V in the web browser will not allow you to select a local printer or map to a local hard drive. If you need that functionality, connect using the Horizon Client or contact your IT team for assistance."
                : label;

              const tooltipBgColor = isLMSIVV ? "#2DA0EB" : undefined;
              const tooltipPlacement = isLMSIVV ? "left" : "top";
              const tooltipWidth = isLMSIVV ? "150px" : "auto";

              return (
                imgSrc &&
                label && (
                  <Application
                    key={imgSrc}
                    imgSrc={imgSrc}
                    fallbackImgSrc={fallbackImgSrc}
                    label={label}
                    subText={subText}
                    url={url}
                    navigateTo={
                      group?.id?.startsWith("App-LMS IV/V")
                        ? () => navigateToNewOpenWindowLMS(group.id)
                        : group?.id === "App-CAC"
                          ? navigateToNewOpenWindowCAC
                          : group?.id === "App-NextGen"
                            ? navigateToNextGen
                            : group?.id === "App-QuickSight"
                              ? navQuickSightEmbeddedUrl
                              : group?.id === "App-CAH-New"
                                ? loadHTMLFormatAppStream
                                : group?.id === "App-CAH-Horizon"
                                  ? navigateToOpenVMW
                                  : navigateTo
                    }
                    tooltipText={tooltipText}
                    tooltipBgColor={tooltipBgColor}
                    tooltipPlacement={tooltipPlacement}
                    tooltipWidth={tooltipWidth}
                  />
                )
              );
            })}

            {(hasCAC || hasCAHNew) && !hasVMWare && (
              <Box sx={{ opacity: 0.6, pointerEvents: "none", margin: "20px" }}>
                <Application
                  imgSrc={`${ICON_BASE_URL}Horizon.png`}
                  label={t("COYOTE_ANALYTICS")}
                  subText={t("PRACTICE_MANAGEMENT_REPORTS")}
                  url=""
                  navigateTo={() => {}}
                />
              </Box>
            )}
          </Grid>
        </Box>
      );
    } else {
      return <></>;
    }
  };
  return (
    <div className="dvContainer">
      <TopMenu
        onMenuItemClicked={onMenuItemClicked}
        name={getName(userInfo)}
        email={userInfo?.email || ""}
        loginType={userInfo?.loginType}
      />
      <Box className={`${coyoteCssClass}`}>
        {coyoteCssClass ? (
          <img
            alt=""
            className="imgLogoSurepoint"
            src={`${ICON_BASE_URL}SurePoint_Logo_Trademark.svg`}
          />
        ) : (
          <img
            alt=""
            className="imgLogoSurepoint"
            src={`${ICON_BASE_URL}SurePoint_Logo_Trademark.svg`}
          />
        )}
        {coyoteCssClass === "coyoteCssClassCover" ? (
          <div className="coyoteTitle">{t("COYOTE_PRACTICE_MANAGEMENT")}</div>
        ) : (
          <div className="lmsTitle">{t("LMS_ACCOUNT")}</div>
        )}
        <Box className="landingPageContainer">
          {renderApplications(userInfo)}
        </Box>
        {userInfo &&
          userInfo.groupsNames &&
          userInfo.groupsNames.findIndex((group) => group === "Admins") !==
            -1 && (
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              className="userManagementdv"
            >
              <Button
                data-testid="userMngtButton"
                onClick={() => {
                  navigateToUserManagement();
                }}
                variant="text"
                sx={{
                  color: "#344054",
                  fontWeight: 400,
                  fontSize: 15,
                  textTransform: "none",
                  "&:hover": { backgroundColor: "transparent" },
                }}
              >
                <img
                  alt=""
                  className="toolManegement"
                  src={`${ICON_BASE_URL}toolsettingicon.svg`}
                />
                <Typography className="landingColorText3">
                  &nbsp;&nbsp;&nbsp;{t("USER_MANAGEMENT")}
                </Typography>
              </Button>
            </Stack>
          )}
        {userInfo?.groupsNames?.some((group) =>
          ["App-CAC", "App-NextGen", "App-CAH-New", "App-CAH-Horizon"].includes(
            group,
          ),
        ) && (
          <Box sx={{ textAlign: "center", padding: "20px" }}>
            <Typography
              component="a"
              href="https://sp-ca-downloads.s3.us-east-2.amazonaws.com/Coyote+Analytics+Horizon+Final+Notice_Login+Screen.pdf"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: "14px",
                color: "#1976d2",
                cursor: "pointer",
                textDecoration: "underline",
                display: "block",
                marginBottom: "8px",
              }}
            >
              📄 Click here to read the official notice about the upcoming
              change.📄
            </Typography>

            <Typography
              sx={{
                fontSize: "14px",
                color: "#1976d2",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => setShowVideo(true)}
            >
              📢 Learn more about Finance Core's new access point powered by
              Horizon 📢
            </Typography>
            {showVideo && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: "rgba(0,0,0,0.7)",
                  zIndex: 1300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ position: "relative", width: "80%", height: "80%" }}>
                  <iframe
                    src="https://player.vimeo.com/video/1072311238?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    title="Finance Core Horizon Access"
                  ></iframe>
                  <Button
                    variant="contained"
                    onClick={() => setShowVideo(false)}
                    sx={{ position: "absolute", top: 10, right: 10 }}
                  >
                    Close
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </div>
  );
}
